'use client';

import { useState } from 'react';
import { Search } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PlayerCard } from '@/components/coach/player-card';
import { EvaluationModal } from '@/components/coach/evaluation-modal';
import { PlayerDetailModal } from '@/components/coach/player-detail-modal';
import { SkillLevelFilter } from '@/components/shared/skill-level-filter';
import { BallLevelFilter } from '@/components/shared/ball-level-filter';
import { useCoachPlayers } from '@/hooks/use-coach-data';
import { useDebounce } from '@/hooks/use-debounce';
import type { Player } from '@/shared/types/coach.types';

export default function PlayersPage() {
  const [searchInput, setSearchInput] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [ballLevelFilter, setBallLevelFilter] = useState<string>('');
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [evalModalOpen, setEvalModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const debouncedSearch = useDebounce(searchInput, 300);

  const { data, isLoading } = useCoachPlayers(debouncedSearch, levelFilter, ballLevelFilter);

  const handleQuickEval = (player: Player) => {
    setSelectedPlayer(player);
    setEvalModalOpen(true);
  };

  const handlePlayerClick = (player: Player) => {
    setSelectedPlayer(player);
    setDetailModalOpen(true);
  };

  const handleNewEvaluationFromDetail = () => {
    setDetailModalOpen(false);
    setEvalModalOpen(true);
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Players" />
      {/* Search and Filter Bar */}
      <div className="flex flex-wrap gap-2">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search players..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-10"
          />
        </div>
        <SkillLevelFilter value={levelFilter} onChange={setLevelFilter} />
        <BallLevelFilter value={ballLevelFilter} onChange={setBallLevelFilter} />
      </div>

      {/* Player List */}
      <div className="grid gap-4">
        {isLoading ? (
          <>
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </>
        ) : data?.players.length === 0 ? (
          <div className="bg-card rounded-lg shadow p-8 text-center">
            <p className="text-muted-foreground">
              {debouncedSearch || levelFilter || ballLevelFilter
                ? 'No players match your filters'
                : 'No players assigned to you yet'}
            </p>
          </div>
        ) : (
          data?.players.map(player => (
            <PlayerCard
              key={player.id}
              player={player}
              onClick={() => handlePlayerClick(player)}
              onQuickEval={() => handleQuickEval(player)}
            />
          ))
        )}
      </div>

      {/* Player Detail Modal */}
      <PlayerDetailModal
        player={selectedPlayer}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        onNewEvaluation={handleNewEvaluationFromDetail}
      />

      {/* Evaluation Modal */}
      <EvaluationModal
        player={selectedPlayer}
        open={evalModalOpen}
        onOpenChange={setEvalModalOpen}
      />
    </div>
  );
}
